import React from 'react';
import { Store, Product, PreviewChannel, WorkspaceView } from '../types';

interface StoresViewProps {
  active: boolean;
  stores: Store[];
  products: Product[];
  activeStoreId: string;
  onSelectStoreId: (storeId: string) => void;
  onPreviewChannelAndView: (channel: PreviewChannel, view: WorkspaceView) => void;
  onOpenAmazonConnect: (triggerEl: HTMLElement | null) => void;
  onOpenNewStore: (triggerEl: HTMLElement | null) => void;
}

export const StoresView: React.FC<StoresViewProps> = ({
  active,
  stores,
  products,
  activeStoreId,
  onSelectStoreId,
  onPreviewChannelAndView,
  onOpenAmazonConnect,
  onOpenNewStore
}) => {
  return (
    <div id="view-stores" className={`workspace-view ${active ? 'active' : ''}`} role="tabpanel" aria-labelledby="tab-stores" hidden={!active}>
      <section className="view-section">
        <div className="container">
          <div className="section-bar">
            <div>
              <span className="section-kicker">CONNECTED CHANNELS</span>
              <h2>
                Your Online Stores (<span id="stores-view-count" className="tabular">{stores.length}</span>)
              </h2>
            </div>
            <div className="preview-controls">
              <button
                type="button"
                id="open-amazon-connect-btn-2"
                className="btn-outline"
                onClick={(e) => onOpenAmazonConnect(e.currentTarget)}
              >
                Configure Amazon SP-API Keys
              </button>
              <button
                type="button"
                id="open-new-store-btn-2"
                className="btn-jet"
                onClick={(e) => onOpenNewStore(e.currentTarget)}
              >
                + Launch New Storefront
              </button>
            </div>
          </div>

          <div id="stores-grid" className="stores-grid" aria-live="polite">
            {stores.map((s) => {
              const isActive = activeStoreId === s.id;
              const syncedCount = products.filter((p) => (Array.isArray(p.assignedStores) ? p.assignedStores.includes(s.id) : true)).length;
              const rawChannel = s.channelKey || 'amazon';
              const targetPreview: PreviewChannel = ['amazon', 'shopify', 'tiktok', 'walmart'].includes(rawChannel)
                ? (rawChannel as PreviewChannel)
                : 'amazon';

              return (
                <article key={s.id} className={`store-card ${isActive ? 'active-store' : ''}`}>
                  <div className="store-card-top">
                    <span className="store-channel-pill">{s.channel}</span>
                    <span className="store-status-pill">
                      <span aria-hidden="true">●</span> Active
                    </span>
                  </div>
                  <div>
                    <h3 className="truncate">{s.name}</h3>
                    <p className="store-url-text truncate">{s.storefrontUrl}</p>
                  </div>
                  <div className="store-metrics-row">
                    <div>
                      <span className="spec-micro-label">CHANNEL FEE</span>
                      <span className="spec-val tabular">{s.referralRateLabel}</span>
                    </div>
                    <div>
                      <span className="spec-micro-label">EST. MONTHLY REV</span>
                      <span className="spec-val tabular">
                        {s.monthlyRevenueFormatted || '$' + (s.monthlyRevenueEst || 25000).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="store-fulfillment-text">
                    <strong>Fulfillment:</strong> {s.fulfillment} (<span className="tabular">{syncedCount}</span> products synced)
                  </div>
                  <div className="store-card-actions">
                    <button
                      type="button"
                      className="btn-jet select-store-btn"
                      data-store-id={s.id}
                      aria-pressed={isActive ? 'true' : 'false'}
                      onClick={() => onSelectStoreId(s.id)}
                    >
                      {isActive ? '✓ Selected Store' : 'Select Store'}
                    </button>
                    <button
                      type="button"
                      className="btn-outline preview-store-channel-btn"
                      data-channel={rawChannel}
                      onClick={() => onPreviewChannelAndView(targetPreview, 'preview')}
                    >
                      Preview Storefront
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};
